/**
 * Black Box — generalized replay. Bundled INTO blackbox.js so any page that
 * loads the tracer can re-enact a recorded session when ?replay=<id> is present.
 *
 * Re-enacts events on the LIVE DOM (ghost cursor, typing, clicks, scroll,
 * overlays). Page-agnostic by design:
 *   - drives elements by the SELECTOR recorded in each event (`ev.el`)
 *   - does NOT require any page-specific global. It will USE window.Steps.goTo
 *     for step transitions IF the page exposes it (progressive enhancement),
 *     otherwise it relies on the replayed clicks to advance the page.
 *   - fills inputs with neutral filler sized to the recorded length (we only
 *     ever recorded `len`, never the value).
 */
import { DEFAULT_CONFIG } from './schema.js';

// Origins allowed as the replay's query-API host (#bbapi): our own platform
// (*.blackboks.ai over https), the baked API base, or localhost in dev.
function trustedApi(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') return true;
    if (u.protocol !== 'https:') return false;
    if (u.hostname === 'blackboks.ai' || u.hostname.endsWith('.blackboks.ai')) return true;
    const baked = (typeof __BB_API_BASE__ !== 'undefined' && __BB_API_BASE__) || '';
    return !!baked && new URL(baked).origin === u.origin;
  } catch (_) { return false; }
}

export function runReplay(userConfig) {
  const params = new URLSearchParams(location.search);
  const sid = params.get('replay');
  if (!sid) return;
  let speed = Number(params.get('speed')) || 1; // default 1× (real-ish pace); bump via the HUD
  const config = Object.assign({}, DEFAULT_CONFIG, userConfig || {});
  for (const k of ['steps', 'fields', 'errors']) {
    config[k] = Object.assign({}, DEFAULT_CONFIG[k], (userConfig && userConfig[k]) || {});
  }
  // #bbtoken = auth token, #bbapi = query-API origin (platform, not the baked
  // collector base). Strip the fragment once read so the token isn't left in the URL.
  var token = null, apiOverride = null;
  try {
    var h = location.hash || '';
    var mt = h.match(/bbtoken=([^&]+)/); if (mt) token = decodeURIComponent(mt[1]);
    var ma = h.match(/bbapi=([^&]+)/); if (ma) apiOverride = decodeURIComponent(ma[1]);
    if (mt || ma) history.replaceState(null, '', location.pathname + location.search);
  } catch (_) {}

  // Only trust #bbapi when it points at OUR platform — otherwise a crafted link
  // could feed the replay a fake session from any server and drive the page.
  if (apiOverride && !trustedApi(apiOverride)) apiOverride = null;
  const apiBase = apiOverride || config.apiBase || (location.port === '5173' ? 'http://localhost:8787' : '');

  fetch(apiBase + '/api/sessions/' + sid, token ? { headers: { Authorization: 'Bearer ' + token } } : undefined)
    .then(function (r) { return r.json(); })
    .then(function (session) {
      if (!session || !session.events) return;
      waitForApp(function () { run(session); });
    })
    .catch(function (e) { console.error('[replay] fetch failed', e); });

  function waitForApp(cb) {
    var tries = 0;
    var stepKeys = Object.keys(config.steps || {});
    var firstStep = stepKeys.length ? config.steps[stepKeys[0]] : null;
    var iv = setInterval(function () {
      var ready = window.Steps || (firstStep && document.querySelector(firstStep)) || document.body;
      if (ready || tries > 50) { clearInterval(iv); setTimeout(cb, 400); }
      tries++;
    }, 100);
  }

  // ---------- visuals ----------
  function esc(v) { return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }
  function el(html) { var d = document.createElement('div'); d.innerHTML = html.trim(); return d.firstChild; }

  var cursor, hud, hudEvent, hudProgress, hudSpeed, overlay;
  function buildChrome(session) {
    var style = el('<style>' +
      '.bb-cursor{position:fixed;z-index:2147483600;width:22px;height:22px;border-radius:50%;' +
      'background:rgba(56,189,248,.35);border:2px solid #38bdf8;box-shadow:0 0 16px 4px rgba(56,189,248,.6);' +
      'transform:translate(-50%,-50%);transition:left .45s cubic-bezier(.4,0,.2,1),top .45s cubic-bezier(.4,0,.2,1);pointer-events:none;left:50%;top:40%}' +
      '.bb-cursor.click{animation:bbclick .3s ease}' +
      '@keyframes bbclick{0%{transform:translate(-50%,-50%) scale(1)}50%{transform:translate(-50%,-50%) scale(.55)}100%{transform:translate(-50%,-50%) scale(1)}}' +
      '.bb-hud{position:fixed;z-index:2147483600;left:50%;top:14px;transform:translateX(-50%);display:flex;align-items:center;gap:12px;' +
      'background:#0f172aee;color:#e2e8f0;border:1px solid #334155;border-radius:999px;padding:8px 16px;font:600 13px/1 ui-sans-serif,system-ui;box-shadow:0 8px 30px rgba(0,0,0,.5)}' +
      '.bb-dot{width:9px;height:9px;border-radius:50%;background:#ef4444;animation:bbpulse 1.2s infinite}' +
      '@keyframes bbpulse{0%,100%{opacity:1}50%{opacity:.3}}' +
      '.bb-bar{width:120px;height:5px;background:#334155;border-radius:999px;overflow:hidden}' +
      '.bb-bar>i{display:block;height:100%;width:0;background:#38bdf8;transition:width .2s}' +
      '.bb-ov{position:fixed;inset:0;z-index:2147483500;background:#020617ee;color:#e2e8f0;display:flex;flex-direction:column;' +
      'align-items:center;justify-content:center;gap:14px;font:600 22px/1.4 ui-sans-serif,system-ui;text-align:center;opacity:0;' +
      'transition:opacity .3s;pointer-events:none;padding:24px}' +
      '.bb-ov.show{opacity:1}.bb-ov .sub{font-size:14px;color:#94a3b8;font-weight:400}' +
      '.bb-flash{outline:3px solid #f59e0b !important;outline-offset:2px;transition:outline .15s}' +
      '.bb-shake{animation:bbshake .4s}' +
      '@keyframes bbshake{0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}' +
      '</style>');
    document.head.appendChild(style);

    cursor = el('<div class="bb-cursor"></div>');
    document.body.appendChild(cursor);

    hud = el('<div class="bb-hud"><span class="bb-dot"></span><span>REPLAY</span>' +
      '<span title="Network faked, navigation blocked — no real calls fire" style="background:#14532d;color:#86efac;border:1px solid #166534;border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700">SANDBOX</span>' +
      '<span style="color:#64748b;font-weight:400">' + esc(session.device) + ' / ' + esc(session.browser) + '</span>' +
      '<span class="bb-speed" title="Click to speed up" style="cursor:pointer;user-select:none;color:#7dd3fc;font-weight:700;padding:0 2px">' + speed + '×</span>' +
      '<span class="bb-ev" style="color:#7dd3fc">starting…</span>' +
      '<span class="bb-bar"><i></i></span></div>');
    document.body.appendChild(hud);
    hudEvent = hud.querySelector('.bb-ev');
    hudProgress = hud.querySelector('.bb-bar > i');
    hudSpeed = hud.querySelector('.bb-speed');

    overlay = el('<div class="bb-ov"><div class="big"></div><div class="sub"></div></div>');
    document.body.appendChild(overlay);
  }

  // Move the ghost cursor to the target. If the event carries the recorded tap
  // position (ev.x/ev.y, document px), land on that exact spot within the element;
  // otherwise fall back to the element's centre.
  function moveCursorTo(target, ev) {
    if (!cursor) return;
    try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
    var place = function () {
      var r;
      try { r = target.getBoundingClientRect(); } catch (_) { return; }
      if (!r || (r.width === 0 && r.height === 0)) return;
      var left = r.left + r.width / 2, top = r.top + r.height / 2;
      if (ev && typeof ev.x === 'number' && typeof ev.y === 'number') {
        var sx = window.scrollX || window.pageXOffset || 0;
        var sy = window.scrollY || window.pageYOffset || 0;
        var offX = ev.x - (r.left + sx), offY = ev.y - (r.top + sy); // recorded offset within the element
        if (offX >= -4 && offY >= -4 && offX <= r.width + 4 && offY <= r.height + 4) { left = r.left + offX; top = r.top + offY; }
      }
      cursor.style.left = left + 'px';
      cursor.style.top = top + 'px';
    };
    place();
    setTimeout(place, 420); // re-place once the smooth scroll settles
  }
  function clickPulse() { if (cursor) { cursor.classList.add('click'); setTimeout(function () { cursor.classList.remove('click'); }, 300); } }

  function showOverlay(big, sub) {
    if (!overlay) return;
    overlay.querySelector('.big').textContent = big;
    overlay.querySelector('.sub').textContent = sub || '';
    overlay.classList.add('show');
  }
  function hideOverlay() { if (overlay) overlay.classList.remove('show'); }

  // ---------- DOM helpers ----------
  var lastEl = null; // last element the user interacted with (for generic error/shake)
  function q(sel) { try { return sel && document.querySelector(sel); } catch (_) { return null; } }
  // Like q(), but when the recorded event carries a sibling index, pick THAT match
  // (so a click on the 3rd FAQ item replays the 3rd, not the first).
  function qn(sel, nth) {
    if (nth == null) return q(sel);
    try { var all = document.querySelectorAll(sel); return all[nth] || all[0] || null; } catch (_) { return q(sel); }
  }
  function fire(node, type) { node.dispatchEvent(new Event(type, { bubbles: true })); }

  // Point where this replayed click should land in the CURRENT viewport.
  // Prefer the recorded page coordinate when it still falls inside the target;
  // otherwise use the target centre as a safe fallback.
  function clickPoint(node, ev, rect) {
    var x = rect.left + rect.width / 2;
    var y = rect.top + rect.height / 2;
    if (ev && typeof ev.x === 'number' && typeof ev.y === 'number') {
      var sx = window.scrollX || window.pageXOffset || 0;
      var sy = window.scrollY || window.pageYOffset || 0;
      var rx = ev.x - sx, ry = ev.y - sy;
      if (rx >= rect.left - 4 && ry >= rect.top - 4 &&
          rx <= rect.right + 4 && ry <= rect.bottom + 4) {
        x = rx; y = ry;
      }
    }
    return { x, y };
  }

  function replayChrome(node) {
    try { return node && node.closest && node.closest('.bb-hud,.bb-cursor,.bb-ov'); } catch (_) { return null; }
  }

  // The replay HUD is intentionally above the host page. Ignore it during
  // hit-testing so our own chrome cannot make a legitimate page click look covered.
  function pageElementAt(x, y) {
    try {
      if (document.elementsFromPoint) {
        var stack = document.elementsFromPoint(x, y);
        for (var i = 0; i < stack.length; i++) if (!replayChrome(stack[i])) return stack[i];
        return null;
      }
      var top = document.elementFromPoint(x, y);
      return replayChrome(top) ? null : top;
    } catch (_) { return null; }
  }

  // Return null when a real pointer could activate this target, otherwise the
  // reason the synthetic click must be skipped. Programmatic node.click() ignores
  // visibility, overlays and pointer-events, which can mutate a page into a state
  // the recorded user never reached.
  function blockedReason(node, ev) {
    try {
      if (node.disabled || node.getAttribute('aria-disabled') === 'true') return 'disabled';
      var style = window.getComputedStyle(node);
      if (!style || style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || style.pointerEvents === 'none') return 'hidden';

      // Opacity is not inherited, so an invisible ancestor is not reflected in
      // the target's computed opacity. Walking to the root is cheap (replay only).
      for (var p = node; p && p.nodeType === 1; p = p.parentElement) {
        var ps = window.getComputedStyle(p);
        if (ps && Number(ps.opacity) === 0) return 'transparent';
      }

      var r = node.getBoundingClientRect();
      if (!r || r.width <= 0 || r.height <= 0) return 'zero-size';
      var point = clickPoint(node, ev, r);
      if (point.x < 0 || point.y < 0 || point.x >= window.innerWidth || point.y >= window.innerHeight) return 'off-screen';

      var top = pageElementAt(point.x, point.y);
      // elementFromPoint commonly returns a span/svg inside a button; that is
      // still the same real click target.
      if (!top || (top !== node && !node.contains(top))) return 'covered';
      return null;
    } catch (_) {
      // If the host DOM throws, fail safe: never fire a blind synthetic click.
      return 'unverifiable';
    }
  }

  function guardedClick(node, ev) {
    var reason = blockedReason(node, ev);
    if (reason) {
      var selector = (ev && ev.el) || node.id || node.tagName || 'target';
      try { console.warn('[replay] skipped ' + reason + ' click:', selector); } catch (_) {}
      return '⚠ skipped: ' + reason;
    }
    try { node.click(); } catch (_) {}
    return null;
  }

  function gen(pool, n) { var s = ''; for (var i = 0; i < n; i++) s += pool[i % pool.length]; return s; }

  // Reproduce a value of the right SHAPE (we only ever recorded the length, never
  // the real value). Format-aware by the input's type/inputmode/autocomplete so
  // common validators pass — otherwise a "valid email" field stays invalid and
  // its submit button never enables.
  function fillValue(node, len) {
    var n = len || 4;
    var attr = function (a) { return (node.getAttribute && (node.getAttribute(a) || '')) || ''; };
    var type = attr('type'), im = attr('inputmode'), ac = attr('autocomplete');
    var s;
    if (type === 'email' || im === 'email' || /email/.test(ac)) {
      s = gen('abcdefghijklmnopqrstuvwxyz', Math.max(1, n - 8)) + '@mail.com';
    } else if (/tel|number/.test(type) || /numeric|decimal|tel/.test(im) || /cc-number|cc-csc|tel|one-time-code/.test(ac)) {
      s = gen('0123456789', n);
    } else {
      s = gen('abcdefghijklmnopqrstuvwxyz', n);
    }
    try { node.value = s; fire(node, 'input'); } catch (_) {}
  }

  function scrollToDepth(pct) {
    if (typeof pct !== 'number') return;
    var doc = document.documentElement;
    var max = doc.scrollHeight - window.innerHeight;
    if (max > 0) window.scrollTo({ top: max * (pct / 100), behavior: 'smooth' });
  }

  // Step index from config order (phone=0, otp=1, …) — matches a goTo(index) API.
  function stepIndexFor(name) {
    var keys = Object.keys(config.steps || {});
    var i = keys.indexOf(name);
    return i >= 0 ? i : 0;
  }
  function goToStep(name) {
    try {
      if (window.Steps && typeof window.Steps.goTo === 'function') {
        window.Steps.goTo(stepIndexFor(name));
      }
      // else: the replayed click on the step's submit button advances the page.
    } catch (_) {}
  }

  function flash(node) { node.classList.add('bb-flash'); setTimeout(function () { node.classList.remove('bb-flash'); }, 350); }
  function shake(node) { node.classList.add('bb-shake'); setTimeout(function () { node.classList.remove('bb-shake'); }, 400); }
  function flashError(field, msg) {
    // Reveal a configured error slot if the page declares one; always shake the
    // element the user was last touching (generic, no page-specific selectors).
    // The page fills its error text from the live API response, which replay never
    // gets — so put back the (scrubbed) message the user saw, if it was recorded.
    var errSel = config.errors && config.errors[field];
    var errNode = errSel && q(errSel);
    if (errNode) {
      if (msg) { try { errNode.textContent = msg; } catch (_) {} }
      errNode.classList.remove('hidden');
      flash(errNode.closest('.input-container') || errNode);
    }
    if (lastEl) shake(lastEl);
  }

  // ---------- per-event actions ----------
  function perform(ev) {
    switch (ev.e) {
      case 'focus': { var n = q(ev.el); if (n) { lastEl = n; moveCursorTo(n); n.focus(); } break; }
      case 'click': { var n = qn(ev.el, ev.nth); if (n) { lastEl = n; moveCursorTo(n, ev); clickPulse(); flash(n); return guardedClick(n, ev); } break; }
      case 'input': { var n = q(ev.el); if (n) { lastEl = n; moveCursorTo(n); fillValue(n, ev.len); } break; }
      case 'paste': { var n = q(ev.el); if (n) { lastEl = n; moveCursorTo(n); fillValue(n, ev.len); } break; }
      case 'input_cleared': { var n = q(ev.el); if (n) { try { n.value = ''; fire(n, 'input'); } catch (_) {} } break; }
      case 'scroll': { scrollToDepth(ev.depth); break; }
      case 'rage_click': { var n = qn(ev.el, ev.nth); if (n) { lastEl = n; moveCursorTo(n, ev); var skipped = null; for (var k = 0; k < 3; k++) { skipped = guardedClick(n, ev); if (skipped) break; } shake(n); return skipped; } break; }
      case 'validation_err': { flashError(ev.field, ev.msg); break; }
      case 'step_change': { goToStep(ev.to); break; }
      case 'tab_hidden': { showOverlay('🔄 User switched away from the page', 'tab hidden — e.g. reading an SMS / another app'); break; }
      case 'tab_visible': { hideOverlay(); break; }
      default: break;
    }
  }

  // ---------- sandbox: fake the network + block navigation ----------
  // Installed at run() time (AFTER the page has loaded and rendered its first
  // step) so the page's own initial fetches are untouched — only the calls made
  // by re-enacted clicks are intercepted.
  function installSandbox() {
    const okPayload = { status: 'success', payload: {} };
    const okBody = JSON.stringify(okPayload);
    const makeRes = function () {
      try { return new Response(okBody, { status: 200, headers: { 'Content-Type': 'application/json' } }); }
      catch (_) {
        return { ok: true, status: 200, headers: { get: function () { return 'application/json'; } },
          json: function () { return Promise.resolve(okPayload); }, text: function () { return Promise.resolve(okBody); },
          clone: function () { return this; } };
      }
    };
    // Let the replay driver's own reads (session + site config) still reach the API.
    const passthrough = function (url) { return url.indexOf('/api/sessions/') !== -1 || url.indexOf('/config?') !== -1; };

    const realFetch = window.fetch;
    window.fetch = function (input) {
      let url = ''; try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (_) {}
      if (passthrough(url)) return realFetch.apply(this, arguments);
      return Promise.resolve(makeRes());
    };

    try {
      function FakeXHR() {}
      FakeXHR.prototype.open = function (m, u) { this._url = u || ''; };
      FakeXHR.prototype.setRequestHeader = function () {};
      FakeXHR.prototype.getAllResponseHeaders = function () { return ''; };
      FakeXHR.prototype.getResponseHeader = function () { return null; };
      FakeXHR.prototype.addEventListener = function (t, cb) { if (t === 'load') this.onload = cb; if (t === 'readystatechange') this.onreadystatechange = cb; };
      FakeXHR.prototype.abort = function () {};
      FakeXHR.prototype.send = function () {
        const self = this;
        setTimeout(function () {
          self.readyState = 4; self.status = 200; self.responseText = okBody; self.response = okBody;
          try { if (typeof self.onreadystatechange === 'function') self.onreadystatechange(); } catch (_) {}
          try { if (typeof self.onload === 'function') self.onload(); } catch (_) {}
        }, 0);
      };
      window.XMLHttpRequest = FakeXHR;
    } catch (_) {}
    try { if (navigator.sendBeacon) navigator.sendBeacon = function () { return true; }; } catch (_) {}

    // Block real navigation but let the page's own handlers run (so faked-success
    // flows still advance): preventDefault only, never stopPropagation.
    const guard = function (e) {
      if (e.type === 'submit') { e.preventDefault(); return; }
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (a) { const href = a.getAttribute('href') || ''; if (href && href[0] !== '#' && !/^javascript:/i.test(href)) e.preventDefault(); }
    };
    document.addEventListener('submit', guard, true);
    document.addEventListener('click', guard, true);
  }

  // ---------- runner ----------
  function run(session) {
    // Sandbox: network faked + navigation blocked, so a replay never fires a real
    // backend call (charges, OTP sends) or leaves the page; success-shaped fakes let
    // success-gated funnels still advance. ALWAYS on — a replay link is just a URL
    // anyone can craft, so it must never be able to opt out.
    try { installSandbox(); } catch (e) { console.warn('[replay] sandbox init failed', e); }
    buildChrome(session);
    // click the speed badge to cycle 1× → 2× → 4× (applies from the next event)
    var levels = [1, 2, 4];
    if (hudSpeed) hudSpeed.addEventListener('click', function () {
      speed = levels[(levels.indexOf(speed) + 1) % levels.length];
      hudSpeed.textContent = speed + '×';
    });
    var events = session.events;
    var i = 0;
    function next() {
      if (i >= events.length) { return finish(session); }
      var ev = events[i];
      var prev = i > 0 ? events[i - 1] : null;
      var gap = (prev && typeof ev.t === 'number' && typeof prev.t === 'number') ? (ev.t - prev.t) / speed : 250;
      gap = Math.min(Math.max(gap, 180), 1600);
      setTimeout(function () {
        var replayStatus = null;
        try { replayStatus = perform(ev); } catch (e) { console.warn('[replay]', ev.e, e); }
        if (hudEvent) hudEvent.textContent = replayStatus || (ev.e + (ev.el ? ' ' + ev.el : '') + (ev.reason ? ' (' + ev.reason + ')' : ''));
        if (hudProgress) hudProgress.style.width = Math.round(((i + 1) / events.length) * 100) + '%';
        i++;
        next();
      }, gap);
    }
    next();
  }

  function finish(session) {
    var converted = session.converted === 1;
    if (hudEvent) hudEvent.textContent = 'done';
    setTimeout(function () {
      showOverlay(
        converted ? '✅ Converted' : '🚫 Abandoned at ' + (session.last_step || 'checkout'),
        converted ? 'The user completed the purchase.' : 'This session never finished — replay complete.'
      );
    }, 500);
  }
}
